import React from 'react';
import { translate } from '../../../localization';
import styles from './NoDataInline.module.css';

/**
 * Enum representing the different variants of the `NoDataInline` component.
 */
export enum NoDataInlineVariant {
  /** Variant for large inline fields with no data */
  LARGE_INLINE_FIELD = 'LARGE_INLINE_FIELD',
  /** Variant for link fields when no link is available */
  LINK_INLINE_FIELD = 'LINK_INLINE_FIELD',
  /** Variant for scenario when data is not available for a table */
  NO_TABLE_DATA = 'NO_TABLE_DATA',
  /** Variant for scenario when data is not available for a medium-width field in a table */
  MEDIUM_TABLE_FIELD = 'MEDIUM_TABLE_FIELD',
  /** Variant for scenario when data is not available for a small-width field in a table */
  SMALL_TABLE_FIELD = 'SMALL_TABLE_FIELD',
  /** Variant for displaying a user-defined custom message */
  CUSTOM_MESSAGE = 'CUSTOM_MESSAGE',
}

/**
 * Base props for the `NoDataInline` component.
 */
interface NoDataInlineBaseProps {
  /** Specifies the variant of the `NoDataInline` component */
  variant: NoDataInlineVariant;
}

/**
 * Props specifically for the `CUSTOM_MESSAGE` variant.
 */
interface NoDataInlineCustomMessageProps extends NoDataInlineBaseProps {
  variant: NoDataInlineVariant.CUSTOM_MESSAGE;
  /** Custom message to display when the `CUSTOM_MESSAGE` variant is selected */
  customMessage: string;
}

/**
 * Props for all variants except `CUSTOM_MESSAGE`.
 */
interface NoDataInlineStandardProps extends NoDataInlineBaseProps {
  variant: Exclude<NoDataInlineVariant, NoDataInlineVariant.CUSTOM_MESSAGE>;
  /** Prevents passing customMessage when the variant is not `CUSTOM_MESSAGE` */
  customMessage?: never;
}

/**
 * Props type that dynamically enforces correct prop usage based on the variant.
 */
type NoDataInlineProps =
  | NoDataInlineCustomMessageProps
  | NoDataInlineStandardProps;

/**
 * A component that displays a message when no data is available for a given field type.
 *
 * - Displays predefined messages for standard variants.
 * - Accepts a custom message only when the `CUSTOM_MESSAGE` variant is used.
 *
 * @component
 * @param {NoDataInlineProps} props - The properties passed to the component.
 * @returns {JSX.Element} The `NoDataInline` component.
 */
const NoDataInline: React.FC<NoDataInlineProps> = ({
  variant,
  customMessage,
}) => {
  const renderNoTableDataScenario = () => {
    return (
      <div className={styles.noTableDataContainer}>
        <span className={styles.noTableDataHeading}>
          {translate('common_no_data_available')}
        </span>
        <span className={styles.noTableDataSubtext}>
          {translate('no_data_message_large_field')}
        </span>
      </div>
    );
  };

  const noDataMessages: Record<
    Exclude<NoDataInlineVariant, NoDataInlineVariant.CUSTOM_MESSAGE>,
    string | JSX.Element
  > = {
    [NoDataInlineVariant.LARGE_INLINE_FIELD]: translate(
      'no_data_message_large_field'
    ),
    [NoDataInlineVariant.LINK_INLINE_FIELD]: translate('no_link_available'),
    [NoDataInlineVariant.NO_TABLE_DATA]: renderNoTableDataScenario(),
    [NoDataInlineVariant.MEDIUM_TABLE_FIELD]: translate('no_data_available'),
    [NoDataInlineVariant.SMALL_TABLE_FIELD]: translate('not_available'),
  };

  return (
    <div className={styles.noDataInlineMessage}>
      {variant === NoDataInlineVariant.CUSTOM_MESSAGE
        ? customMessage
        : noDataMessages[variant] || translate('no_data_available')}
    </div>
  );
};

export default NoDataInline;
